import { FaPlay } from '@react-icons/all-files/fa/FaPlay';
import clsx from 'clsx';
import { useContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ISanityImage, TSectionVideo } from 'typescriptDefs';

import { useIsMobile, useWindowWidth } from 'helpers';
import useLoadAsyncReactPlayer, { isYoutubeLink } from 'helpers/loadAsync/useLoadAsyncReactPlayer';

import { GatsbyImg, LoaderPropagate } from '.';
import { LocaleContext } from './rebrand/Layout';

const transformCenter = 'absolute top-1/2 left-1/2 -translate-x-[50%] -translate-y-[50%]';

const VideoLoaderResponsive = ({
  altText,
  className,
  customIcon,
  imgThumbnailDesktop,
  imgThumbnailMobile,
  mobileAspectRatio = 1,
  noOverlay = false,
  urlVideoDesktop,
  urlVideoMobile,
}: TSectionVideo & { noOverlay?: boolean }) => {
  const isMobile = useWindowWidth() < 768;
  const locale = useContext(LocaleContext);
  const [readyToPlay, setReadyToPlay] = useState<boolean>(false);
  const [clickedPreview, setClickedPreview] = useState<boolean>(false);

  const videoUrl = isMobile ? urlVideoMobile : urlVideoDesktop;
  const ReactPlayer = useLoadAsyncReactPlayer(clickedPreview, isYoutubeLink(videoUrl));

  let imgDataMobile = imgThumbnailMobile;
  let imgDataDesktop = imgThumbnailDesktop;

  if ((imgThumbnailMobile as ISanityImage)?.asset?.gatsbyImageData) {
    imgDataMobile = (imgThumbnailMobile as ISanityImage).asset.gatsbyImageData;
  }

  if ((imgThumbnailDesktop as ISanityImage)?.asset?.gatsbyImageData) {
    imgDataDesktop = (imgThumbnailDesktop as ISanityImage).asset.gatsbyImageData;
  }

  return (
    <div
      className={twMerge(
        clsx(
          'relative z-0 block h-full w-full pb-0',
          clickedPreview && `aspect-video sm:aspect-video`,
          clickedPreview && isMobile && `aspect-[var(--mobile-apect-ratio)] pb-[var(--mobile-aspect-ratio-pb)]`,
          isMobile && 'block md:hidden',
          className,
        ),
      )}
      style={
        {
          '--mobile-apect-ratio': mobileAspectRatio,
          '--mobile-aspect-ratio-pb': `${mobileAspectRatio * 100}%`,
        } as React.CSSProperties
      }
    >
      <div className={twMerge(clsx('flex justify-center', clickedPreview && 'hidden'))}>
        <div
          className={twMerge(
            clsx(
              'relative z-10 cursor-pointer opacity-100 transition-opacity duration-1000 ease-out',
              clickedPreview && readyToPlay && 'pointer-events-none z-[-1] opacity-0',
            ),
          )}
          onClick={() => setClickedPreview(true)}
        >
          {isMobile ? (
            <GatsbyImg
              className={twMerge(
                clsx(
                  'relative flex h-full w-full sm:h-auto',
                  !noOverlay &&
                    "after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black after:opacity-20 after:content-['']",
                ),
              )}
              image={imgDataMobile}
              alt={altText ?? { en: 'Video thumbnail', fr: 'Vignette vidéo' }[locale]}
            />
          ) : (
            <GatsbyImg
              className={twMerge(
                clsx(
                  'relative flex aspect-video h-full w-full sm:h-auto',
                  !noOverlay &&
                    "after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black after:opacity-20 after:content-['']",
                ),
              )}
              image={imgDataDesktop}
              alt={altText ?? { en: 'Video thumbnail', fr: 'Vignette vidéo' }[locale]}
            />
          )}
          {customIcon ? (
            customIcon
          ) : (
            <FaPlay className={twMerge('text-[1.5rem] text-[#F7F8FD] sm:text-xl md:text-[2.5rem]', transformCenter)} />
          )}
        </div>
      </div>
      {clickedPreview && ReactPlayer ? (
        <>
          <div className={twMerge('z-10 opacity-100 transition-opacity', readyToPlay && 'opacity-0', transformCenter)}>
            <LoaderPropagate />
          </div>
          <ReactPlayer.default
            width="100%"
            height="100%"
            controls={true}
            autoPlay={true}
            playsinline={true}
            playing={readyToPlay}
            onReady={() => setReadyToPlay(true)}
            url={videoUrl}
            className="absolute transition-opacity duration-[0.3s] ease-in"
            style={{ opacity: readyToPlay ? 1 : 0 }}
          />
        </>
      ) : null}
    </div>
  );
};

export default VideoLoaderResponsive;

type TReactPlayer = {
  muted: boolean;
};

export const VideoLoaderResponsiveAutoplay = ({
  videoMobileURL,
  videoDesktopURL,
  mobileAspectRatio = 1,
  videoProps,
  ReactPlayer,
  className,
  ...props
}: {
  videoMobileURL: string;
  videoDesktopURL: string;
  mobileAspectRatio?: number;
  videoProps?: TReactPlayer;
  className?: string;
  ReactPlayer: any;
}) => {
  const [readyToPlay, readyToPlaySet] = useState(false);
  const [started, startedSet] = useState(false);
  const mobile = useIsMobile();

  return (
    <div
      className={twMerge(
        clsx(
          'relative h-full w-full pb-[56.25%] md:pb-[56.25%]',
          mobile && `pb-[${mobileAspectRatio * 100}%]`,
          className,
        ),
      )}
      {...props}
    >
      {!started ? (
        <div className={twMerge('transition-opacity ease-in', transformCenter)}>
          <LoaderPropagate />
        </div>
      ) : null}
      {mobile && ReactPlayer && (
        <ReactPlayer.default
          controls={true}
          muted={false}
          loop={true}
          playsinline={true}
          width="100%"
          height="100%"
          autoPlay={true}
          playing={readyToPlay}
          url={videoMobileURL}
          onReady={() => readyToPlaySet(true)}
          onStart={() => startedSet(true)}
          className={twMerge(clsx('absolute transition-opacity duration-1000 ease-out'))}
          style={{
            opacity: readyToPlay ? 1 : 0,
          }}
          {...videoProps}
        />
      )}
      {!mobile && ReactPlayer && (
        <ReactPlayer.default
          onReady={() => readyToPlaySet(true)}
          onStart={() => startedSet(true)}
          autoPlay={true}
          controls={true}
          muted={true}
          loop={true}
          playing={readyToPlay}
          width="100%"
          height="100%"
          url={videoDesktopURL}
          className={twMerge(clsx('absolute transition-all duration-1000 ease-out'))}
          style={{
            transform: `translateY(${started ? 0 : 100}px)`,
            opacity: started ? 1 : 0,
          }}
          {...videoProps}
        />
      )}
    </div>
  );
};
